/**
 * Formats a unix timestamp (milliseconds or seconds) or ISO date string to a human-readable date
 */
export const formatDate = (dateValue?: string | number, includeTime: boolean = false): string => {
  if (!dateValue) return 'Unknown date';

  try {
    let date: Date;

    // Check if it's a unix timestamp (number or numeric string)
    if (typeof dateValue === 'number') {
      // Determine if it's in seconds or milliseconds
      // Timestamps before year 2001 in milliseconds would be < 1000000000000
      // Timestamps after year 2001 in seconds would be > 1000000000
      // If the number is less than 10000000000, it's likely in seconds
      const timestamp = dateValue < 10000000000 ? dateValue * 1000 : dateValue;
      date = new Date(timestamp);
    } else if (typeof dateValue === 'string' && /^\d+$/.test(dateValue)) {
      // Numeric string, determine if in seconds or milliseconds
      const numValue = parseInt(dateValue);
      const timestamp = numValue < 10000000000 ? numValue * 1000 : numValue;
      date = new Date(timestamp);
    } else {
      // ISO string or other date format
      date = new Date(dateValue);
    }

    // Check if date is valid
    if (isNaN(date.getTime())) {
      return String(dateValue);
    }

    const options: Intl.DateTimeFormatOptions = {
      year: 'numeric',
      month: 'short',
      day: 'numeric',
    };

    if (includeTime) {
      options.hour = '2-digit';
      options.minute = '2-digit';
      // Omit timeZoneName — it makes the string too wide for table cells.
      // Callers that need it can use formatLogTimestamp or absTime.
    }

    return date.toLocaleDateString('en-US', options);
  } catch {
    return String(dateValue);
  }
};

/**
 * Formats a unix timestamp (milliseconds) to a full date time string
 */
export const formatDateTime = (timestamp?: string | number): string => {
  return formatDate(timestamp, true);
};

/**
 * Returns a relative timestamp ("5d ago", "2h ago") with the absolute date
 * available for use as a title/tooltip.
 */
export const relativeTime = (dateValue?: string | number): { relative: string; absolute: string } => {
  if (!dateValue) return { relative: '—', absolute: '—' };
  try {
    let date: Date;
    if (typeof dateValue === 'number') {
      const ts = dateValue < 10_000_000_000 ? dateValue * 1000 : dateValue;
      date = new Date(ts);
    } else if (typeof dateValue === 'string' && /^\d+$/.test(dateValue)) {
      const n = parseInt(dateValue);
      date = new Date(n < 10_000_000_000 ? n * 1000 : n);
    } else {
      date = new Date(dateValue);
    }
    if (isNaN(date.getTime())) return { relative: String(dateValue), absolute: String(dateValue) };

    const absolute = date.toLocaleString('en-US', {
      month: 'short', day: 'numeric', year: 'numeric',
      hour: '2-digit', minute: '2-digit', timeZoneName: 'short',
    });

    const diffSec = Math.floor((Date.now() - date.getTime()) / 1000);
    let relative: string;
    if (diffSec < 60) relative = `${diffSec}s ago`;
    else if (diffSec < 3600) relative = `${Math.floor(diffSec / 60)}m ago`;
    else if (diffSec < 86400) relative = `${Math.floor(diffSec / 3600)}h ago`;
    else if (diffSec < 86400 * 30) relative = `${Math.floor(diffSec / 86400)}d ago`;
    else relative = absolute;

    return { relative, absolute };
  } catch {
    return { relative: String(dateValue), absolute: String(dateValue) };
  }
};

/**
 * Formats a timestamp for logs with more detail
 */
export const formatLogTimestamp = (timestamp: number): string => {
  try {
    const date = new Date(timestamp);
    return date.toLocaleString('en-US', {
      month: 'short',
      day: 'numeric',
      year: 'numeric',
      hour: '2-digit',
      minute: '2-digit',
      second: '2-digit',
      timeZoneName: 'short',
    });
  } catch {
    return String(timestamp);
  }
};
